import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PHONE_NUMBER = '0584727893';

const Footer = () => {
  const { t } = useTranslation();

  const handleCall = () => {
    window.location.href = `tel:${PHONE_NUMBER}`;
  };

  const handleCopyPhone = () => {
    navigator.clipboard.writeText(PHONE_NUMBER).catch(() => {});
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-primary-500 rounded-xl flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <span className="font-bold text-xl text-white">Nhà Trọ<span className="text-primary-400">KTX</span></span>
            </Link>
            <p className="text-gray-400 text-sm">{t('footer.tagline')}</p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm">{t('nav.home')}</Link>
              </li>
              <li>
                <Link to="/find-by-request" className="text-gray-400 hover:text-white transition-colors text-sm">{t('nav.findByRequest')}</Link>
              </li>
            </ul>
          </div>

          {/* Contact Us */}
          <div>
            <h3 className="font-semibold text-white mb-4">{t('footer.contact')}</h3>
            <div className="space-y-3">
              {/* Phone Number - Main CTA */}
              <div className="group relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 via-primary-400 to-primary-600 rounded-xl blur-sm opacity-0 group-hover:opacity-75 transition-all duration-300" />
                <div className="relative flex items-center gap-3 bg-gray-800 group-hover:bg-gray-750 rounded-xl p-3 border border-gray-700 group-hover:border-primary-500/50 transition-all duration-300 cursor-pointer" onClick={handleCall}>
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400">{t('footer.phone')}</p>
                    <p className="text-lg font-bold text-white group-hover:text-primary-300 transition-colors tracking-wide">
                      <span className="text-primary-400">058</span>4 727 893
                    </p>
                  </div>
                  {/* Ring animation dots */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-ping" style={{ animationDuration: '1.2s' }} />
                    <div className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-ping" style={{ animationDuration: '1.2s', animationDelay: '0.2s' }} />
                    <div className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-ping" style={{ animationDuration: '1.2s', animationDelay: '0.4s' }} />
                  </div>
                </div>
              </div>

              {/* Copy & Call action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleCall}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-primary-600/25 active:scale-95"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {t('detail.contact')}
                </button>
                <button
                  onClick={handleCopyPhone}
                  className="inline-flex items-center justify-center px-3 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white text-sm font-medium rounded-lg border border-gray-700 hover:border-gray-600 transition-all duration-200 active:scale-95"
                  title={t('common.copy')}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>

              {/* Working Hours */}
              <div className="flex items-center gap-2 text-xs text-gray-500 pt-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('footer.workingHoursValue')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Nhà Trọ KTX. {t('footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;